import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  getHrisPenggajian, getHrisPenggajianById, batchHrisPenggajian, updateHrisPenggajian,
  deleteHrisPenggajian, approveHrisPenggajian, rejectHrisPenggajian, bayarHrisPenggajian, getKaryawan,
} from '../../api';
import { useAuth } from '../../AuthContext';
import {
  Plus, CheckCircle, XCircle, Wallet, Printer, Eye, Pencil, Trash2, Search,
  CalendarRange, CalendarDays, DollarSign, X, FileText,
} from 'lucide-react';
import logoPerusahaan from '../../utils/logoPerusahaan';

// ============ Helper periode (Bulanan = YYYY-MM, Mingguan = YYYY-MM-DD Senin) ============
const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const toISODate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parseISODate = (s) => {
  const [y, m, d] = (s || '').split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};
const mondayOf = (dateLike) => {
  const d = dateLike instanceof Date ? new Date(dateLike) : parseISODate(dateLike);
  const day = d.getDay(); // 0=Min
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return toISODate(d);
};
const fmtPeriode = (periode, jenis) => {
  if (!periode) return '-';
  if (jenis === 'Mingguan' || periode.length === 10) {
    const start = parseISODate(periode);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    const opsi = { day: '2-digit', month: 'short' };
    return `${start.toLocaleDateString('id-ID', opsi)} – ${end.toLocaleDateString('id-ID', { ...opsi, year: 'numeric' })}`;
  }
  return parseISODate(periode + '-01').toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });
};
const fmtRp = (v) => 'Rp ' + Number(v || 0).toLocaleString('id-ID');

// ---------- Input nominal dengan pemisah ribuan (5.000.000) ----------
// State tetap menyimpan digit mentah ('5000000'), tampilan diberi titik.
const angkaDari = (v) => {
  if (typeof v === 'number') return isFinite(v) ? Math.round(v) : 0;
  const digit = String(v ?? '').replace(/\D/g, '');
  return digit ? Number(digit) : 0;
};
const fmtRibuan = (v) => {
  if (typeof v === 'number') return isFinite(v) && v !== 0 ? Math.round(v).toLocaleString('id-ID') : '';
  const digit = String(v ?? '').replace(/\D/g, '');
  if (!digit) return '';
  return Number(digit).toLocaleString('id-ID');
};

// Input teks bernuansa angka: menampilkan 5.000.000 dan caret tetap di posisi wajar.
function InputNominal({ value, onChange, style, className, placeholder = '0' }) {
  const ref = useRef(null);
  const handleChange = (e) => {
    const el = e.target;
    const digitBeforeCaret = el.value.slice(0, el.selectionStart ?? 0).replace(/\D/g, '').length;
    const digit = el.value.replace(/\D/g, '');
    onChange(digit);
    requestAnimationFrame(() => {
      const node = ref.current;
      if (!node) return;
      const formatted = fmtRibuan(digit);
      let pos = 0;
      let seen = 0;
      while (pos < formatted.length && seen < digitBeforeCaret) {
        if (/\d/.test(formatted[pos])) seen += 1;
        pos += 1;
      }
      try { node.setSelectionRange(pos, pos); } catch (err) { /* abaikan */ }
    });
  };
  return (
    <input
      ref={ref}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      value={fmtRibuan(value)}
      onChange={handleChange}
      placeholder={placeholder}
      className={className}
      style={style}
    />
  );
}
const fmtTgl = (s) => {
  if (!s) return '-';
  const d = parseISODate(String(s).slice(0, 10));
  return isNaN(d.getTime()) ? String(s).slice(0, 10) : d.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
};

const STATUS_BADGE = {
  Pengajuan: { bg: '#fff5ee', color: '#c2410c', label: 'Menunggu ACC' },
  Disetujui: { bg: '#eef2ff', color: '#3730a3', label: 'Gaji ACC' },
  Ditolak: { bg: '#fef2f2', color: '#991b1b', label: 'Ditolak' },
  Lunas: { bg: '#d1fae5', color: '#065f46', label: 'Lunas' },
};

const KOMPONEN_BULANAN = [
  { key: 'gaji_pokok', label: 'Gaji Pokok' },
  { key: 'tunjangan_jabatan', label: 'Tunj. Jabatan' },
  { key: 'insentif_keahlian_ac', label: 'Insentif AC' },
  { key: 'insentif_project', label: 'Insentif Project' },
  { key: 'lembur', label: 'Lembur' },
  { key: 'potongan_alpa', label: 'Potongan Alpa', potongan: true },
  { key: 'potongan_kasbon', label: 'Kasbon', potongan: true },
];
const KOMPONEN_MINGGUAN = [
  { key: 'tunjangan_transport_bbm', label: 'Transport BBM' },
  { key: 'tunjangan_sewa_kendaraan', label: 'Sewa Kendaraan' },
];

const emptyRow = () => {
  const r = { karyawan_id: '', catatan: '' };
  [...KOMPONEN_BULANAN, ...KOMPONEN_MINGGUAN].forEach(k => { r[k.key] = ''; });
  return r;
};

const hitungRow = (row, jenis) => {
  const komponen = jenis === 'Mingguan' ? KOMPONEN_MINGGUAN : KOMPONEN_BULANAN;
  const num = (v) => angkaDari(v);
  if (jenis === 'Mingguan') {
    return komponen.reduce((s, k) => s + num(row[k.key]), 0);
  }
  const masuk = komponen.filter(k => !k.potongan).reduce((s, k) => s + num(row[k.key]), 0);
  const keluar = komponen.filter(k => k.potongan).reduce((s, k) => s + num(row[k.key]), 0);
  return masuk - keluar;
};

export default function HrisPenggajian() {
  const { user } = useAuth();
  const canApprove = ['admin', 'manager'].includes(user?.role);

  const [jenis, setJenis] = useState('Bulanan');
  const [periode, setPeriode] = useState(thisMonth());
  const [tab, setTab] = useState('pengajuan');
  const [data, setData] = useState([]);
  const [ringkasan, setRingkasan] = useState(null);
  const [karyawan, setKaryawan] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  // Modal input pengajuan (single / grid massal)
  const [showInput, setShowInput] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [rows, setRows] = useState([emptyRow()]);
  const [saving, setSaving] = useState(false);

  // Modal detail / bayar / tolak
  const [detail, setDetail] = useState(null);
  const [bayarItem, setBayarItem] = useState(null);
  const [bayarForm, setBayarForm] = useState({ tanggal: toISODate(new Date()), jumlah: '', metode_pembayaran: 'Transfer', catatan: '' });
  const [rejectItem, setRejectItem] = useState(null);
  const [rejectCatatan, setRejectCatatan] = useState('');

  const fetchData = () => {
    setLoading(true);
    setError('');
    const params = { jenis };
    if (jenis === 'Bulanan') params.periode_bulan = periode;
    else params.periode = periode;
    getHrisPenggajian(params)
      .then(res => {
        setData(res.data?.data || []);
        setRingkasan(res.data?.ringkasan || null);
      })
      .catch(e => setError(e.response?.data?.error || 'Gagal memuat data penggajian'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [jenis, periode]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    getKaryawan().then(res => setKaryawan(res.data || [])).catch(() => {});
  }, []);

  const gantiJenis = (j) => {
    setJenis(j);
    setPeriode(j === 'Mingguan' ? mondayOf(new Date()) : thisMonth());
    setTab('pengajuan');
  };

  const rowsTab = useMemo(() => {
    const byStatus = {
      pengajuan: ['Pengajuan', 'Ditolak'],
      acc: ['Disetujui'],
      laporan: ['Lunas'],
    }[tab] || [];
    return data
      .filter(d => byStatus.includes(d.status))
      .filter(d => !search
        || (d.nama || '').toLowerCase().includes(search.toLowerCase())
        || (d.bagian || '').toLowerCase().includes(search.toLowerCase()));
  }, [data, tab, search]);

  const jumlahTab = (key) => {
    const byStatus = { pengajuan: ['Pengajuan', 'Ditolak'], acc: ['Disetujui'], laporan: ['Lunas'] }[key] || [];
    return data.filter(d => byStatus.includes(d.status)).length;
  };

  // ============ Input pengajuan ============
  const bukaInput = () => {
    setEditingId(null);
    setRows([emptyRow()]);
    setShowInput(true);
  };

  const bukaEdit = async (item) => {
    try {
      const res = await getHrisPenggajianById(item.id);
      const d = res.data;
      const row = emptyRow();
      row.karyawan_id = String(d.karyawan_id);
      row.catatan = d.catatan || '';
      // Simpan sebagai digit mentah ('5000000') agar input berformat ribuan konsisten
      [...KOMPONEN_BULANAN, ...KOMPONEN_MINGGUAN].forEach(k => {
        row[k.key] = d[k.key] === null || d[k.key] === undefined ? '' : String(angkaDari(d[k.key]));
      });
      setEditingId(d.id);
      setRows([row]);
      setShowInput(true);
    } catch (e) {
      alert('Gagal memuat detail pengajuan');
    }
  };

  const ubahRow = (idx, field, value) => {
    const next = [...rows];
    next[idx] = { ...next[idx], [field]: value };
    setRows(next);
  };
  const tambahRow = () => setRows([...rows, emptyRow()]);
  const hapusRow = (idx) => {
    if (rows.length <= 1) return;
    setRows(rows.filter((_, i) => i !== idx));
  };

  const simpanPengajuan = async () => {
    const valid = rows.filter(r => r.karyawan_id);
    if (valid.length === 0) { alert('Pilih minimal 1 karyawan'); return; }
    if (new Set(valid.map(r => Number(r.karyawan_id))).size !== valid.length) {
      alert('Ada karyawan yang sama di lebih dari 1 baris'); return;
    }
    const payloadItems = valid.map(r => {
      const item = {
        karyawan_id: Number(r.karyawan_id),
        periode,
        jenis_gaji: jenis,
        catatan: (r.catatan || '').trim(),
        metode_pembayaran: 'Transfer',
      };
      [...KOMPONEN_BULANAN, ...KOMPONEN_MINGGUAN].forEach(k => { item[k.key] = angkaDari(r[k.key]); });
      return item;
    });
    setSaving(true);
    try {
      if (editingId) {
        await updateHrisPenggajian(editingId, payloadItems[0]);
        setShowInput(false);
        fetchData();
        alert('Pengajuan gaji diperbarui');
      } else {
        const res = await batchHrisPenggajian({ items: payloadItems });
        setShowInput(false);
        fetchData();
        alert(res.data?.message || 'Pengajuan gaji tersimpan');
      }
    } catch (e) {
      alert(e.response?.data?.error || 'Gagal menyimpan pengajuan gaji');
    } finally {
      setSaving(false);
    }
  };

  // ============ Aksi lain ============
  const handleApprove = async (item) => {
    if (!window.confirm(`Setujui (ACC) pengajuan gaji ${item.nama} — ${fmtRp(item.thp)}?`)) return;
    try {
      await approveHrisPenggajian(item.id, {});
      fetchData();
    } catch (e) {
      alert(e.response?.data?.error || 'Gagal menyetujui pengajuan');
    }
  };

  const handleReject = async () => {
    if (!rejectItem) return;
    if (!rejectCatatan.trim()) { alert('Alasan penolakan wajib diisi'); return; }
    try {
      await rejectHrisPenggajian(rejectItem.id, { catatan: rejectCatatan.trim() });
      setRejectItem(null);
      setRejectCatatan('');
      fetchData();
    } catch (e) {
      alert(e.response?.data?.error || 'Gagal menolak pengajuan');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Hapus pengajuan gaji ${item.nama} periode ${fmtPeriode(item.periode, item.jenis_gaji)}?`)) return;
    try { await deleteHrisPenggajian(item.id); fetchData(); }
    catch (e) { alert(e.response?.data?.error || 'Gagal menghapus'); }
  };

  const bukaBayar = (item) => {
    setBayarItem(item);
    setBayarForm({
      tanggal: toISODate(new Date()),
      jumlah: String(Math.round(Number(item.sisa_gaji || item.thp || 0))),
      metode_pembayaran: item.metode_pembayaran || 'Transfer',
      catatan: '',
    });
  };

  const simpanBayar = async () => {
    if (!bayarItem) return;
    const jumlah = angkaDari(bayarForm.jumlah);
    if (jumlah <= 0) { alert('Jumlah pembayaran harus lebih dari 0'); return; }
    try {
      const res = await bayarHrisPenggajian(bayarItem.id, {
        tanggal: bayarForm.tanggal,
        jumlah,
        metode_pembayaran: bayarForm.metode_pembayaran,
        catatan: bayarForm.catatan,
      });
      alert(res.data?.message || 'Pembayaran tersimpan');
      setBayarItem(null);
      fetchData();
    } catch (e) {
      alert(e.response?.data?.error || 'Gagal menyimpan pembayaran');
    }
  };

  const bukaDetail = async (item) => {
    try {
      const res = await getHrisPenggajianById(item.id);
      setDetail(res.data);
    } catch (e) {
      alert('Gagal memuat detail');
    }
  };

  // ============ Cetak Laporan Gaji (PDF) ============
  const cetakLaporan = () => {
    const { jsPDF } = require('jspdf');
    const autoTable = require('jspdf-autotable').default || require('jspdf-autotable');
    const doc = new jsPDF('portrait', 'mm', 'a4');
    const margin = 12;

    try { doc.addImage(logoPerusahaan, 'PNG', margin, 8, 30, 16); } catch (e) { /* ignore */ }
    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.text('LAPORAN GAJI', 195, 14, { align: 'right' });
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.text('PT. SYNEXA GLOBAL TEKNO', 195, 19, { align: 'right' });
    doc.text(`Periode: ${fmtPeriode(periode, jenis)} · ${jenis}`, 195, 23.5, { align: 'right' });
    doc.text(`Dicetak: ${new Date().toLocaleString('id-ID')}`, 195, 28, { align: 'right' });

    doc.setDrawColor(17, 205, 239);
    doc.setLineWidth(0.8);
    doc.line(margin, 31, 198, 31);

    const body = rowsTab.map((d, i) => [
      i + 1,
      d.nama || '-',
      d.bagian || '-',
      fmtPeriode(d.periode, d.jenis_gaji),
      Number(d.total_penerimaan || 0).toLocaleString('id-ID'),
      Number(d.total_potongan || 0).toLocaleString('id-ID'),
      Number(d.thp || 0).toLocaleString('id-ID'),
      Number(d.total_dibayar || 0).toLocaleString('id-ID'),
      d.status,
    ]);

    autoTable(doc, {
      startY: 35,
      head: [['No', 'Nama Karyawan', 'Bagian', 'Periode', 'Penerimaan', 'Potongan', 'THP', 'Dibayar', 'Status']],
      body,
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [17, 205, 239], textColor: 255, fontStyle: 'bold' },
      columnStyles: {
        4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' },
      },
      foot: [['', 'TOTAL', '', '',
        Number(rowsTab.reduce((s, d) => s + Number(d.total_penerimaan || 0), 0)).toLocaleString('id-ID'),
        Number(rowsTab.reduce((s, d) => s + Number(d.total_potongan || 0), 0)).toLocaleString('id-ID'),
        Number(rowsTab.reduce((s, d) => s + Number(d.thp || 0), 0)).toLocaleString('id-ID'),
        Number(rowsTab.reduce((s, d) => s + Number(d.total_dibayar || 0), 0)).toLocaleString('id-ID'),
        '']],
      footStyles: { fillColor: [240, 244, 248], textColor: 40, fontStyle: 'bold' },
    });

    const y = (doc.lastAutoTable?.finalY || 60) + 14;
    doc.setFontSize(8);
    doc.text('Dibuat oleh,', 30, y);
    doc.text('Disetujui oleh,', 150, y);
    doc.text('(____________________)', 30, y + 18);
    doc.text('(____________________)', 150, y + 18);

    doc.save(`Laporan_Gaji_${jenis}_${periode}.pdf`);
  };

  // ============ Render ============
  if (loading) return <div className="page-loading"><div className="spinner-lg" /></div>;

  const isMingguan = jenis === 'Mingguan';
  const komponenForm = isMingguan ? KOMPONEN_MINGGUAN : KOMPONEN_BULANAN;
  const adaData = rowsTab.length > 0;

  return (
    <div className="page-container">
      <div className="page-header" style={{ borderBottom: '2px solid var(--gray-100)', paddingBottom: 16, marginBottom: 20 }}>
        <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: 'var(--gray-600)' }}>
          <DollarSign size={22} style={{ marginRight: 8, verticalAlign: 'middle', color: 'var(--cyan-500)' }} />
          Penggajian Karyawan
        </h2>
        <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem', margin: 0 }}>
          Alur: input pengajuan gaji → ACC atasan → pembayaran (boleh sebagian) → laporan & cetak
        </p>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: '0.85rem' }}>
          {error}
        </div>
      )}

      {/* Kartu ringkasan */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 14, marginBottom: 18 }}>
        <div style={{ background: '#fff', borderRadius: 12, padding: '16px 18px', boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', borderLeft: '4px solid #fb6340' }}>
          <div style={{ fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--gray-400)' }}>Pengajuan</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--gray-600)' }}>{ringkasan?.pengajuan || 0}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>{fmtRp(ringkasan?.nominal_pengajuan)}</div>
        </div>
        <div style={{ background: '#fff', borderRadius: 12, padding: '16px 18px', boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', borderLeft: '4px solid #5e72e4' }}>
          <div style={{ fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--gray-400)' }}>Gaji ACC</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--gray-600)' }}>{ringkasan?.acc || 0}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>{fmtRp(ringkasan?.nominal_acc)}</div>
        </div>
        <div style={{ background: '#fff', borderRadius: 12, padding: '16px 18px', boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', borderLeft: '4px solid #f5365c' }}>
          <div style={{ fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--gray-400)' }}>Sisa Belum Dibayar</div>
          <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#991b1b' }}>{fmtRp(ringkasan?.sisa_bayar)}</div>
        </div>
        <div style={{ background: '#fff', borderRadius: 12, padding: '16px 18px', boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', borderLeft: '4px solid #2dce89' }}>
          <div style={{ fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--gray-400)' }}>Lunas</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#065f46' }}>{ringkasan?.lunas || 0}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>{fmtRp(ringkasan?.nominal_lunas)}</div>
        </div>
      </div>

      {/* Toolbar: jenis + periode + aksi */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: '#fff', borderRadius: 12, padding: '12px 16px', marginBottom: 14, boxShadow: '0 0 2rem 0 rgba(136,152,170,0.1)' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {[{ v: 'Bulanan', label: 'Bulanan', icon: CalendarRange }, { v: 'Mingguan', label: 'Mingguan', icon: CalendarDays }].map(j => {
            const Icon = j.icon;
            const aktif = jenis === j.v;
            return (
              <button key={j.v} onClick={() => gantiJenis(j.v)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, cursor: 'pointer',
                  border: `1px solid ${aktif ? '#5e72e4' : '#e2e8f0'}`,
                  background: aktif ? '#5e72e4' : '#fff', color: aktif ? '#fff' : '#475569', fontSize: '0.8rem', fontWeight: 600,
                }}>
                <Icon size={14} /> {j.label}
              </button>
            );
          })}
        </div>

        {isMingguan ? (
          <input type="date" value={periode}
            onChange={e => setPeriode(e.target.value ? mondayOf(e.target.value) : '')}
            style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem' }} />
        ) : (
          <input type="month" value={periode} onChange={e => setPeriode(e.target.value)}
            style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem' }} />
        )}
        <span style={{ fontSize: '0.8rem', color: 'var(--gray-400)' }}>{fmtPeriode(periode, jenis)}</span>

        <div style={{ flex: 1 }} />
        <div style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 9, top: 10, color: '#94a3b8' }} />
          <input type="text" placeholder="Cari karyawan / bagian" value={search} onChange={e => setSearch(e.target.value)}
            style={{ padding: '7px 10px 7px 28px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem', width: 200 }} />
        </div>
        <button className="btn btn-primary" onClick={bukaInput} style={{ whiteSpace: 'nowrap' }}>
          <Plus size={15} /> Input Pengajuan Gaji
        </button>
      </div>

      {/* Tab alur */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {[
          { key: 'pengajuan', label: 'Pengajuan Gaji', color: '#fb6340' },
          { key: 'acc', label: 'Gaji ACC', color: '#5e72e4' },
          { key: 'laporan', label: 'Laporan Gaji', color: '#2dce89' },
        ].map(t => {
          const aktif = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, cursor: 'pointer',
                border: `1px solid ${aktif ? t.color : '#e2e8f0'}`, background: aktif ? t.color : '#fff',
                color: aktif ? '#fff' : '#475569', fontWeight: 600, fontSize: '0.82rem',
              }}>
              {t.label}
              <span style={{
                background: aktif ? 'rgba(255,255,255,0.25)' : '#f1f5f9', borderRadius: 10,
                padding: '1px 7px', fontSize: '0.72rem',
              }}>{jumlahTab(t.key)}</span>
            </button>
          );
        })}
        <div style={{ flex: 1 }} />
        {tab === 'laporan' && (
          <button className="btn btn-secondary" onClick={cetakLaporan} disabled={!adaData}>
            <Printer size={15} /> Cetak Laporan Gaji
          </button>
        )}
      </div>

      {/* Tabel list view */}
      <div className="table-wrapper" style={{ background: '#fff', borderRadius: 12, boxShadow: '0 0 2rem 0 rgba(136,152,170,0.1)' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Karyawan</th>
              <th>Bagian</th>
              <th>Periode</th>
              <th>Jenis</th>
              <th style={{ textAlign: 'right' }}>Penerimaan</th>
              <th style={{ textAlign: 'right' }}>Potongan</th>
              <th style={{ textAlign: 'right' }}>THP</th>
              <th style={{ textAlign: 'right' }}>Dibayar</th>
              <th style={{ textAlign: 'right' }}>Sisa</th>
              <th>Status</th>
              <th style={{ width: 190 }}>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {!adaData && (
              <tr>
                <td colSpan={11} style={{ textAlign: 'center', padding: 28, color: 'var(--gray-400)', fontSize: '0.85rem' }}>
                  {tab === 'pengajuan' && 'Belum ada pengajuan gaji pada periode ini. Klik "Input Pengajuan Gaji".'}
                  {tab === 'acc' && 'Belum ada pengajuan yang di-ACC pada periode ini.'}
                  {tab === 'laporan' && 'Belum ada gaji yang lunas pada periode ini.'}
                </td>
              </tr>
            )}
            {rowsTab.map(d => {
              const badge = STATUS_BADGE[d.status] || { bg: '#f3f4f6', color: '#374151', label: d.status };
              return (
                <tr key={d.id}>
                  <td style={{ fontWeight: 600, fontSize: '0.85rem' }}>{d.nama || '-'}</td>
                  <td style={{ fontSize: '0.8rem', color: 'var(--gray-400)' }}>{d.bagian || '-'}</td>
                  <td style={{ fontSize: '0.8rem' }}>{fmtPeriode(d.periode, d.jenis_gaji)}</td>
                  <td style={{ fontSize: '0.8rem' }}>{d.jenis_gaji}</td>
                  <td style={{ textAlign: 'right', fontSize: '0.8rem' }}>{fmtRp(d.total_penerimaan)}</td>
                  <td style={{ textAlign: 'right', fontSize: '0.8rem', color: '#991b1b' }}>{fmtRp(d.total_potongan)}</td>
                  <td style={{ textAlign: 'right', fontSize: '0.85rem', fontWeight: 700 }}>{fmtRp(d.thp)}</td>
                  <td style={{ textAlign: 'right', fontSize: '0.8rem', color: '#065f46' }}>{fmtRp(d.total_dibayar)}</td>
                  <td style={{ textAlign: 'right', fontSize: '0.8rem', color: (d.sisa_gaji || 0) > 0 ? '#c2410c' : '#64748b' }}>{fmtRp(d.sisa_gaji)}</td>
                  <td>
                    <span style={{ background: badge.bg, color: badge.color, padding: '3px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 600 }}>
                      {badge.label}
                    </span>
                    {d.status === 'Ditolak' && d.catatan && (
                      <div style={{ fontSize: '0.68rem', color: '#991b1b', marginTop: 3 }}>{d.catatan}</div>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <button className="btn-icon" title="Detail" onClick={() => bukaDetail(d)} style={{ color: 'var(--cyan-600)' }}>
                        <Eye size={15} />
                      </button>
                      {tab === 'pengajuan' && (
                        <>
                          {canApprove && d.status === 'Pengajuan' && (
                            <>
                              <button className="btn-icon" title="ACC / Setujui" onClick={() => handleApprove(d)} style={{ color: '#065f46' }}>
                                <CheckCircle size={15} />
                              </button>
                              <button className="btn-icon" title="Tolak" onClick={() => { setRejectItem(d); setRejectCatatan(''); }} style={{ color: '#dc2626' }}>
                                <XCircle size={15} />
                              </button>
                            </>
                          )}
                          <button className="btn-icon" title="Edit" onClick={() => bukaEdit(d)} style={{ color: '#5e72e4' }}>
                            <Pencil size={15} />
                          </button>
                          <button className="btn-icon" title="Hapus" onClick={() => handleDelete(d)} style={{ color: '#f5365c' }}>
                            <Trash2 size={15} />
                          </button>
                        </>
                      )}
                      {tab === 'acc' && (
                        <button className="btn btn-primary" style={{ padding: '5px 10px', fontSize: '0.75rem' }} onClick={() => bukaBayar(d)}>
                          <Wallet size={13} /> Bayar
                        </button>
                      )}
                      {tab === 'laporan' && (
                        <button className="btn-icon" title="Detail pembayaran" onClick={() => bukaDetail(d)} style={{ color: '#5e72e4' }}>
                          <FileText size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ============ MODAL INPUT PENGAJUAN ============ */}
      {showInput && (
        <div className="modal-overlay" style={{ padding: 0 }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowInput(false); }}>
          <div className="modal" style={{
            maxWidth: '100vw', width: '100vw', height: '100vh', maxHeight: '100vh',
            borderRadius: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}>
            <div className="modal-header" style={{ padding: '16px 20px 0' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>
                {editingId ? 'Edit Pengajuan Gaji' : `Input Pengajuan Gaji — ${jenis} · ${fmtPeriode(periode, jenis)}`}
              </h3>
              <button className="btn-icon" onClick={() => setShowInput(false)}><X size={18} /></button>
            </div>
            <div className="modal-body" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: '14px 20px' }}>
              <table className="data-table excel-grid table-fit gaji-grid">
                  <thead>
                    <tr>
                      <th style={{ width: '14%' }}>Karyawan</th>
                      {komponenForm.map(k => <th key={k.key}>{k.label}</th>)}
                      <th style={{ width: 120, textAlign: 'right' }}>THP</th>
                      <th style={{ width: '11%' }}>Keterangan</th>
                      <th style={{ width: 36 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, idx) => (
                      <tr key={idx}>
                        <td>
                          <select value={r.karyawan_id} onChange={e => ubahRow(idx, 'karyawan_id', e.target.value)}
                            className="gaji-cell"
                            style={{ width: '100%', minWidth: 0, border: '1px solid #d1d5db', borderRadius: 6 }}>
                            <option value="">-- Pilih Karyawan --</option>
                            {karyawan.map(k => (
                              <option key={k.id} value={k.id}>{k.nama}{k.bagian_name || k.bagian ? ` — ${k.bagian_name || k.bagian}` : ''}</option>
                            ))}
                          </select>
                        </td>
                        {komponenForm.map(k => (
                          <td key={k.key}>
                            <InputNominal
                              value={r[k.key]}
                              onChange={(digit) => ubahRow(idx, k.key, digit)}
                              placeholder="0"
                              className="gaji-nominal"
                              style={{
                                width: '100%', minWidth: 0,
                                border: '1px solid #d1d5db', borderRadius: 6,
                                fontWeight: 500, textAlign: 'right',
                              }} />
                          </td>
                        ))}
                        <td style={{ textAlign: 'right', fontWeight: 700, fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{fmtRp(hitungRow(r, jenis))}</td>
                        <td>
                          <input type="text" value={r.catatan} onChange={e => ubahRow(idx, 'catatan', e.target.value)}
                            placeholder="opsional"
                            className="gaji-cell"
                            style={{ width: '100%', minWidth: 0, border: '1px solid #d1d5db', borderRadius: 6 }} />
                        </td>
                        <td>
                          <button className="btn-icon" title="Hapus baris" onClick={() => hapusRow(idx)} style={{ color: '#f5365c' }}>
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              {!editingId && (
                <button className="btn btn-secondary" style={{ marginTop: 10 }} onClick={tambahRow}>
                  <Plus size={14} /> Tambah Baris
                </button>
              )}
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: 10 }}>
                Nominal yang diisi adalah komponen gaji <strong>{jenis.toLowerCase()}</strong>. THP dihitung otomatis
                {jenis === 'Bulanan' ? ' (penerimaan − potongan)' : ' (Transport BBM + Sewa Kendaraan)'}.
              </div>
            </div>
            <div className="modal-footer" style={{ padding: '12px 20px', flexShrink: 0 }}>
              <button className="btn btn-secondary" onClick={() => setShowInput(false)}>Batal</button>
              <button className="btn btn-primary" onClick={simpanPengajuan} disabled={saving}>
                {saving ? 'Menyimpan...' : 'Simpan Pengajuan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============ MODAL BAYAR (boleh sebagian) ============ */}
      {bayarItem && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setBayarItem(null); }}>
          <div className="modal" style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Bayar Gaji — {bayarItem.nama}</h3>
              <button className="btn-icon" onClick={() => setBayarItem(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 14px', marginBottom: 14, fontSize: '0.85rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>THP</span><strong>{fmtRp(bayarItem.thp)}</strong></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Sudah dibayar</span><strong>{fmtRp(bayarItem.total_dibayar)}</strong></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#c2410c' }}><span>Sisa</span><strong>{fmtRp(bayarItem.sisa_gaji)}</strong></div>
              </div>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-500)' }}>Tanggal</label>
              <input type="date" value={bayarForm.tanggal} onChange={e => setBayarForm({ ...bayarForm, tanggal: e.target.value })}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, marginBottom: 10, fontSize: '0.85rem' }} />
              <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-500)' }}>Jumlah bayar (boleh sebagian)</label>
              <InputNominal value={bayarForm.jumlah} onChange={(digit) => setBayarForm({ ...bayarForm, jumlah: digit })}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, marginBottom: 10, fontSize: '0.85rem', textAlign: 'right', fontWeight: 500 }} />
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <button className="btn btn-secondary" style={{ padding: '5px 10px', fontSize: '0.75rem' }}
                  onClick={() => setBayarForm({ ...bayarForm, jumlah: String(Math.round(Number(bayarItem.sisa_gaji || 0))) })}>
                  Bayar Penuh (sisa)
                </button>
              </div>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-500)' }}>Metode</label>
              <select value={bayarForm.metode_pembayaran} onChange={e => setBayarForm({ ...bayarForm, metode_pembayaran: e.target.value })}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, marginBottom: 10, fontSize: '0.85rem' }}>
                <option value="Transfer">Transfer (Bank)</option>
                <option value="Kas">Kas / Tunai</option>
              </select>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-500)' }}>Catatan</label>
              <input type="text" value={bayarForm.catatan} onChange={e => setBayarForm({ ...bayarForm, catatan: e.target.value })}
                placeholder="opsional"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem' }} />
              <div style={{ fontSize: '0.74rem', color: 'var(--gray-400)', marginTop: 10 }}>
                Bila pembayaran ini melunasi THP, status otomatis menjadi <strong>Lunas</strong> dan jurnal gaji dibuat
                (D 6100 Beban Gaji / K Bank·Kas).
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setBayarItem(null)}>Batal</button>
              <button className="btn btn-primary" onClick={simpanBayar}>Simpan Pembayaran</button>
            </div>
          </div>
        </div>
      )}

      {/* ============ MODAL TOLAK ============ */}
      {rejectItem && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setRejectItem(null); }}>
          <div className="modal" style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Tolak Pengajuan Gaji</h3>
              <button className="btn-icon" onClick={() => setRejectItem(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: '0.85rem', marginBottom: 10 }}>
                {rejectItem.nama} — {fmtRp(rejectItem.thp)} ({fmtPeriode(rejectItem.periode, rejectItem.jenis_gaji)})
              </div>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-500)' }}>Alasan penolakan</label>
              <textarea value={rejectCatatan} onChange={e => setRejectCatatan(e.target.value)} rows={3}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem' }} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setRejectItem(null)}>Batal</button>
              <button className="btn btn-primary" onClick={handleReject}>Tolak Pengajuan</button>
            </div>
          </div>
        </div>
      )}

      {/* ============ MODAL DETAIL ============ */}
      {detail && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setDetail(null); }}>
          <div className="modal" style={{ maxWidth: 620 }}>
            <div className="modal-header">
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>
                Detail Penggajian — {detail.nama}
              </h3>
              <button className="btn-icon" onClick={() => setDetail(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: '0.85rem', marginBottom: 14 }}>
                <div><span style={{ color: 'var(--gray-400)' }}>Periode</span><div><strong>{fmtPeriode(detail.periode, detail.jenis_gaji)}</strong></div></div>
                <div><span style={{ color: 'var(--gray-400)' }}>Jenis</span><div><strong>{detail.jenis_gaji}</strong></div></div>
                <div><span style={{ color: 'var(--gray-400)' }}>Status</span><div><strong>{STATUS_BADGE[detail.status]?.label || detail.status}</strong></div></div>
                <div><span style={{ color: 'var(--gray-400)' }}>Di-ACC oleh</span><div><strong>{detail.approved_nama || '-'}</strong></div></div>
              </div>

              <table className="data-table" style={{ marginBottom: 14 }}>
                <thead><tr><th>Komponen</th><th style={{ textAlign: 'right' }}>Nominal</th></tr></thead>
                <tbody>
                  {(detail.jenis_gaji === 'Mingguan' ? KOMPONEN_MINGGUAN : KOMPONEN_BULANAN).map(k => (
                    Number(detail[k.key] || 0) !== 0 && (
                      <tr key={k.key}>
                        <td style={{ fontSize: '0.83rem' }}>{k.label}</td>
                        <td style={{ textAlign: 'right', fontSize: '0.83rem', color: k.potongan ? '#991b1b' : '#111' }}>{fmtRp(detail[k.key])}</td>
                      </tr>
                    )
                  ))}
                  <tr>
                    <td style={{ fontWeight: 700 }}>THP</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtRp(detail.thp)}</td>
                  </tr>
                </tbody>
              </table>

              <div style={{ fontSize: '0.8rem', fontWeight: 700, marginBottom: 6 }}>Riwayat Pembayaran</div>
              {(!detail.pembayaran || detail.pembayaran.length === 0) ? (
                <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)' }}>Belum ada pembayaran.</div>
              ) : (
                <table className="data-table">
                  <thead><tr><th>Tanggal</th><th>Metode</th><th style={{ textAlign: 'right' }}>Jumlah</th><th>Catatan</th></tr></thead>
                  <tbody>
                    {detail.pembayaran.map(b => (
                      <tr key={b.id}>
                        <td style={{ fontSize: '0.8rem' }}>{fmtTgl(b.tanggal)}</td>
                        <td style={{ fontSize: '0.8rem' }}>{b.metode_pembayaran}</td>
                        <td style={{ textAlign: 'right', fontSize: '0.8rem' }}>{fmtRp(b.jumlah)}</td>
                        <td style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>{b.catatan || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, fontSize: '0.85rem' }}>
                <span>Total dibayar: <strong>{fmtRp(detail.total_dibayar)}</strong></span>
                <span>Sisa: <strong style={{ color: '#c2410c' }}>{fmtRp(detail.sisa_gaji)}</strong></span>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDetail(null)}>Tutup</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
